// The ONE form shape the three vendor item write surfaces are built from (HUBX-8474):
// the modal, the simple row and the detailed row.
//
// A factory rather than a base class: the three have different lifecycles — the modal is
// created behind an *ngIf and lives until it closes, a row's form is created when that row
// enters edit and discarded on Undo — and inheritance would force one ngOnInit to serve all
// three. It takes the FormBuilder rather than injecting one, which is what keeps this module
// free of @angular/core and callable from a plain function.
//
// It is also load-bearing for correctness, not just for reuse: dqTierValidator reaches
// UnitPrice by walking group.parent.parent, so a form built by hand that merely looks alike
// silently stops validating the discount tiers. Every surface builds through here.
//
// CONTROL NAMES are the v2 contract's, which renames six of legacy's row controls:
//   status_skuStatus          -> skuStatus
//   inventory_availableToSell -> availability
//   eta                       -> leadTimeDays
//   exwPointValue             -> exwId
//   prices[].unitPrice        -> discountQtys[].price
//   restrictionDescription    -> restrictions
//   comments[]                -> specialNotes
// Stated here as the mapping a reader of the legacy row will need; nothing translates at
// runtime, because nothing on the v2 side speaks the old names.

import { FormBuilder, FormGroup, UntypedFormArray, Validators } from '@angular/forms';
import { dqTierValidator, moqMxqValidator, notBlank, unitPriceCostValidator } from './item-write-validators';

/** Build the write form.
 *
 *  `pricing` is the ROLE gate (ADR-0009 / §8.3): ADMIN and VM price items, a Seller does
 *  not. It decides the initial disabled state of the money and operator-only controls, and
 *  a disabled control drops out of `fg.value` — which is exactly how a Seller is kept from
 *  sending UnitPrice (UNIT_PRICE_NOT_ALLOWED, 403), XID, the DQ tiers and the target
 *  company on a Create.
 *
 *  Per-ITEM permission is a separate and later step: applyWritability narrows this further
 *  from the server's `writableFields` once an item has been read. The role decides what the
 *  form offers; the server decides what this caller may write on this item.
 *
 *  Requiredness follows the CONTRACT as shipped, not the coarse layout asterisks:
 *  VendorItemCode, Mpn, Description, ManufacturerId, CategoryId, Condition, Packaging,
 *  Warranty, ExwId, Availability, LeadTimeDays and Cost are required; MOQ is optional
 *  (absent -> server default 1) and MXQ optional (0/absent = no maximum); XID is optional
 *  and re-defaults server-side (A-R10). Cost is required for EVERY role (PricingPolicy);
 *  UnitPrice only for the pricing roles. */
/** Description's limit: the edge's [MaxLength(250)] (VendorItemWriteRequest), and what the
 *  counter under the field counts against. */
export const DESCRIPTION_MAX_LENGTH = 250;

export function createItemWriteForm(fb: FormBuilder, pricing: boolean): FormGroup {
  const fg = fb.group({
    status: { value: 'Pending', disabled: !pricing },
    vendorItemCode: ['', [Validators.required, Validators.maxLength(50), notBlank]],
    // XID: ADMIN/VM only (§2 amended 2026-07-21). Optional; blank/absent -> A-R10 default
    // (= VendorItemCode), so no required and no blank check.
    hubxItemCode: [{ value: '', disabled: !pricing }, [Validators.maxLength(50)]],
    mpn: ['', [Validators.required, Validators.maxLength(50), notBlank]],
    description: ['', [Validators.required, Validators.maxLength(DESCRIPTION_MAX_LENGTH), notBlank]],
    manufacturerId: ['', Validators.required],
    categoryMainId: '',
    categoryId: ['', Validators.required],
    condition: ['', Validators.required],
    packaging: ['', Validators.required],
    warranty: ['', Validators.required],
    exwId: ['', Validators.required],
    availability: [null, [Validators.required, Validators.min(0)]],
    leadTimeDays: [null, [Validators.required, Validators.min(0)]],
    // MOQ optional (server default 1); MXQ optional (0/absent = "no maximum").
    moq: [null, Validators.min(1)],
    mxq: [null, Validators.min(0)],
    // Cost (UnitPurchasePrice): required for every role.
    cost: [null, [Validators.required, Validators.min(0.01)]],
    // Sale price: ADMIN/VM only; disabled (and thus omitted from fg.value) for SELLER.
    unitPrice: [
      { value: null, disabled: !pricing },
      pricing ? [Validators.required, Validators.min(0.01)] : []
    ],
    restrictions: '',
    specialNotes: '',
    // Collapsed Status (Edit only). Holds an EffectiveStatus, which is a PROJECTION, not a
    // stored member: the edit request turns the chosen value back into the axis it implies.
    // Blank on Create, where it is never rendered and the POST has no status member beyond
    // createApproved.
    skuStatus: '',
    // Today's Deals (Edit only). A real write member of the PUT, and on a HUBX-owned item
    // the ONLY one this caller may move — so without this control that whole case has
    // nothing to do. Blank/false on Create, where the POST does not accept the member.
    todaysDeals: false,
    // Target company (ADR-0010). Disabled for SELLER, which is what keeps the member out of
    // fg.value entirely — the same mechanism as UnitPrice/XID, so a Seller can never send it
    // even if the control were somehow rendered. Never `required`: absent means "the company
    // I'm already in", which is the default and the overwhelmingly common case. Empty
    // string, not null, to match every other select and its `<option value="">` placeholder.
    targetVendorId: [{ value: '', disabled: !pricing }],
    // The item's discount margin (HUBX-8466), read by dqTierValidator for the third-party
    // floor. Not an input and never sent: disabled, it is out of fg.value, applyWritability
    // never maps it, and the request builders name their members.
    discountQtyMarginPercent: [{ value: null, disabled: true }],
    discountQtys: fb.array([0, 1, 2].map(() => fb.group(
      {
        fromQty: { value: null, disabled: !pricing },
        price: { value: null, disabled: !pricing }
      },
      { validators: dqTierValidator }
    )))
  }, {
    validators: [moqMxqValidator, unitPriceCostValidator]
  });

  // The tiers read Cost for the margin floor and Price for the sale-price ceiling (HUBX-8466),
  // but a tier's validator runs only when the tier changes: re-check them when either moves,
  // on every surface that builds the form here (the rows had no Price re-check; the modal kept
  // its own). Without events, so a row's own valueChanges sees the change once.
  const tiers = fg.get('discountQtys') as UntypedFormArray;
  const recheckTiers = () => tiers.controls.forEach(t => t.updateValueAndValidity({ emitEvent: false }));
  fg.controls['cost'].valueChanges.subscribe(recheckTiers);
  fg.controls['unitPrice'].valueChanges.subscribe(recheckTiers);
  // A tier is checked against the others (every tier differs, and more units cost less per
  // unit, HUBX-8466), so a change to one re-checks the rest, earlier slots too.
  tiers.controls.forEach(tier => tier.valueChanges.subscribe(() =>
    tiers.controls.filter(t => t !== tier).forEach(t => t.updateValueAndValidity({ emitEvent: false }))));
  return fg;
}
